/**
 * Renders a parsed Help Center article (src/content/help/markdown.js) as
 * React elements. Never touches HTML strings: whatever an article says is
 * text, and links are only the kinds the parser allows.
 */
import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Info, Lightbulb, AlertTriangle, Quote } from 'lucide-react';
import { cn } from '@/lib/utils';

const LINK_CLASS = 'text-[#AAB4D6] underline decoration-white/25 underline-offset-[3px] hover:decoration-[#AAB4D6] hover:text-white transition-colors';

function Inline({ nodes }) {
  return (nodes || []).map((n, i) => {
    if (n.type === 'text') return <Fragment key={i}>{n.value}</Fragment>;
    if (n.type === 'strong') return <strong key={i} className="font-medium text-white"><Inline nodes={n.children} /></strong>;
    if (n.type === 'link') {
      if (n.href.startsWith('/')) {
        return <Link key={i} to={n.href} className={LINK_CLASS}><Inline nodes={n.children} /></Link>;
      }
      const external = /^https:\/\//i.test(n.href);
      return (
        <a key={i} href={n.href} className={LINK_CLASS} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          <Inline nodes={n.children} />
        </a>
      );
    }
    return null;
  });
}
Inline.propTypes = { nodes: PropTypes.array };

const CALLOUTS = {
  tip: { label: 'Tip', Icon: Lightbulb, box: 'border-emerald-400/20 bg-emerald-400/[0.06]', icon: 'text-emerald-300' },
  note: { label: 'Good to know', Icon: Info, box: 'border-[#7A8ABF]/30 bg-[#7A8ABF]/[0.08]', icon: 'text-[#AAB4D6]' },
  important: { label: 'Important', Icon: AlertTriangle, box: 'border-amber-400/25 bg-amber-400/[0.06]', icon: 'text-amber-300' },
  quote: { label: null, Icon: Quote, box: 'border-white/10 bg-white/[0.03]', icon: 'text-white/40' },
};

const P = 'text-[16.5px] sm:text-[17px] leading-[1.75] text-white/85';

function Block({ block }) {
  switch (block.type) {
    case 'heading':
      return block.level === 2 ? (
        <h2 id={block.id} className="scroll-mt-28 mt-11 mb-3 text-[22px] leading-snug font-medium text-white tracking-[-0.01em]">
          <Inline nodes={block.children} />
        </h2>
      ) : (
        <h3 id={block.id} className="scroll-mt-28 mt-8 mb-2 text-[17px] leading-snug font-medium text-white">
          <Inline nodes={block.children} />
        </h3>
      );
    case 'paragraph':
      return <p className={cn(P, 'my-5')}><Inline nodes={block.children} /></p>;
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag className={cn('my-4 pl-6 space-y-2.5', block.ordered ? 'list-decimal marker:text-white/50' : 'list-disc marker:text-white/35')}>
          {block.items.map((item, i) => <li key={i} className={cn(P, 'pl-1')}><Inline nodes={item} /></li>)}
        </Tag>
      );
    }
    case 'table':
      return (
        <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full border-collapse text-left">
            <thead className="bg-white/[0.04]">
              <tr>
                {block.header.map((c, i) => (
                  <th key={i} scope="col" className="px-4 py-3 text-[13.5px] font-medium text-white/90 border-b border-white/10 whitespace-nowrap"><Inline nodes={c} /></th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.06]">
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((c, i) => <td key={i} className="px-4 py-3 align-top text-[14.5px] leading-relaxed text-white/80"><Inline nodes={c} /></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'callout': {
      const c = CALLOUTS[block.kind] || CALLOUTS.note;
      return (
        <aside className={cn('my-6 flex gap-3 rounded-2xl border px-4 py-3.5', c.box)}>
          <c.Icon className={cn('mt-[3px] h-4 w-4 flex-shrink-0', c.icon)} strokeWidth={1.75} aria-hidden />
          <div className="min-w-0">
            {c.label && <p className="text-[12px] uppercase tracking-[0.14em] font-medium text-white/70">{c.label}</p>}
            {block.children.map((p, i) => <p key={i} className={cn(P, 'mt-1 text-[16px] sm:text-[16px]')}><Inline nodes={p.children} /></p>)}
          </div>
        </aside>
      );
    }
    default:
      return null;
  }
}
Block.propTypes = { block: PropTypes.object.isRequired };

export default function ArticleBody({ blocks }) {
  return <div className="help-article-body">{(blocks || []).map((b, i) => <Block key={i} block={b} />)}</div>;
}
ArticleBody.propTypes = { blocks: PropTypes.array };
